<template>
  <div class="location-page">
    <div class="hero-section">
      <div class="container">
        <h1>Tandarts Berlicum | Tandzorg Gestel | Dichtbij Berlicum en Middelrode</h1>
        <p class="lead">
          Op zoek naar een betrouwbare <strong>tandarts bij Berlicum</strong>? Tandzorg Gestel in Sint-Michielsgestel
          ligt op ongeveer 8 tot 15 minuten van Berlicum centrum (afhankelijk van verkeer). Persoonlijke tandheelkundige zorg met moderne technieken.
        </p>
        <p class="travel-note">Reistijden zijn indicatief en afhankelijk van verkeer en exacte vertreklocatie.</p>
      </div>
    </div>

    <div class="content-section">
      <div class="container">
        <div class="row">
          <div class="col-md-8">
            <h2>Waarom kiezen patiënten uit Berlicum voor Tandzorg Gestel?</h2>
            <p>
              Inwoners van <em>Berlicum</em>, <em>Middelrode</em> en <em>Rosmalen</em> waarderen onze nabije
              locatie, persoonlijke benadering en rustige praktijkomgeving.
            </p>

            <div class="highlight-box">
              <h3>🎯 Waarom Tandzorg Gestel vanuit Berlicum?</h3>
              <div class="row">
                <div class="col-md-6">
                  <ul class="benefits-list">
                    <li><strong>Dichterbij dan u denkt</strong> - Ongeveer 8 tot 15 minuten vanuit Berlicum centrum</li>
                    <li><strong>Geen parkeerstress</strong> - Ruime gratis parking</li>
                    <li><strong>Persoonlijke zorg</strong> - Geen haast, alle tijd voor u</li>
                    <li><strong>Vaste behandelaar</strong> - Continuïteit in uw zorg</li>
                  </ul>
                </div>
                <div class="col-md-6">
                  <ul class="benefits-list">
                    <li><strong>Moderne apparatuur</strong> - Laatste technieken</li>
                    <li><strong>Duidelijke planning</strong> - Doordeweeks goed bereikbaar</li>
                    <li><strong>Complete zorg</strong> - Van preventie tot specialisme</li>
                    <li><strong>Familiepraktijk</strong> - Alle leeftijden welkom</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2>Route en bereikbaarheid vanuit Berlicum</h2>
            <p>Tandzorg Gestel is vanuit verschillende delen van de regio Berlicum goed bereikbaar:</p>

            <div class="route-section">
              <div class="row">
                <div class="col-md-6">
                  <div class="route-card">
                    <h4>🚗 Met de auto</h4>
                    <ul>
                      <li><strong>Vanuit Berlicum centrum:</strong><br>Via N279 richting Sint-Michielsgestel (10 min)</li>
                      <li><strong>Vanuit Middelrode:</strong><br>Via Berlicum naar Sint-Michielsgestel (8 min)</li>
                      <li><strong>Vanuit Rosmalen:</strong><br>Via ring en N279 richting praktijk (15 min)</li>
                      <li><strong>Vanuit Den Dungen:</strong><br>Via lokale routes naar Sint-Michielsgestel (12 min)</li>
                    </ul>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="route-card">
                    <h4>🚌 Openbaar vervoer</h4>
                    <ul>
                      <li><strong>Bus + overstap:</strong><br>Via Den Bosch naar Sint-Michielsgestel (25-35 min)</li>
                      <li><strong>Directe regionale lijn:</strong><br>Afhankelijk van vertrektijd (20-30 min)</li>
                      <li><strong>Fiets:</strong><br>Goed te fietsen via regionale routes (30 min)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <h2>Afspraak maken vanuit Berlicum</h2>
            <p>
              Kort en duidelijk: plan online uw afspraak of neem direct contact met ons op.
            </p>
            <div class="cta-buttons">
              <router-link to="/afspraak-maken" class="btn btn-primary">
                <i class="fas fa-calendar-plus"></i> Afspraak Maken
              </router-link>
              <router-link to="/contact" class="btn btn-secondary">
                <i class="fas fa-phone"></i> Contact
              </router-link>
            </div>
          </div>

          <div class="col-md-4">
            <div class="sidebar">
              <div class="contact-card">
                <h3>📍 Bereikbaarheid</h3>
                <div class="contact-info">
                  <p><strong>Adres:</strong><br>Schijndelseweg 13A<br>5271 BK Sint-Michielsgestel</p>
                  <p><strong>Telefoon:</strong><br>073 - 551 5644</p>
                  <p><strong>Afstand vanuit Berlicum:</strong><br>8 tot 15 minuten rijden</p>
                </div>
              </div>

              <LocalRouteMap
                city-name="Berlicum"
                origin-query="Berlicum, Nederland"
                drive-time="ongeveer 8 tot 15 minuten"
                transit-time="ongeveer 20 tot 30 minuten"
              />

              <div class="hours-card">
                <h3>🕒 Openingstijden</h3>
                <div class="hours-grid">
                  <div class="hours-row"><span class="day">Maandag</span><span class="time">8:00 - 17:00</span></div>
                  <div class="hours-row"><span class="day">Dinsdag</span><span class="time">8:00 - 17:00</span></div>
                  <div class="hours-row"><span class="day">Woensdag</span><span class="time">8:00 - 17:00</span></div>
                  <div class="hours-row"><span class="day">Donderdag</span><span class="time">8:00 - 17:00</span></div>
                  <div class="hours-row"><span class="day">Vrijdag</span><span class="time">8:00 - 17:00</span></div>
                  <div class="hours-row"><span class="day">Zondag</span><span class="time">Gesloten</span></div>
                </div>
              </div>

              <div class="specialties-card">
                <h3>🏆 Onze Specialiteiten</h3>
                <ul class="specialties-list">
                  <li>✓ Lid van De Lieve Tandarts</li>
                  <li>✓ Digitale tandheelkunde</li>
                  <li>✓ Pijn-vrije behandelingen</li>
                  <li>✓ Kindvriendelijke praktijk</li>
                  <li>✓ Moderne apparatuur</li>
                  <li>✓ Ervaren specialisten</li>
                </ul>
              </div>

              <div class="emergency-card">
                <h3>🚨 Spoedgevallen</h3>
                <p>Tandpijn of spoedgeval? Ook vanuit Berlicum helpen we u snel.</p>
                <router-link to="/spoed" class="emergency-btn">
                  <i class="fas fa-exclamation-triangle"></i> Spoedhulp
                </router-link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LocalRouteMap from '../components/LocalRouteMap.vue'

export default {
  name: 'TandartsBerlicum',
  components: {
    LocalRouteMap
  },
  mounted() {
    document.title = 'Tandarts Berlicum | Tandzorg Gestel Sint-Michielsgestel | Ongeveer 8-15 Min Rijden'

    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content',
        'Tandarts Berlicum gezocht? Tandzorg Gestel in Sint-Michielsgestel ligt op ongeveer 8 tot 15 minuten van Berlicum (afhankelijk van verkeer). Persoonlijke zorg, moderne technieken en gratis parkeren.')
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]')
    if (metaKeywords) {
      metaKeywords.setAttribute('content',
        'tandarts Berlicum, tandarts Middelrode, tandarts omgeving Berlicum, tandarts Sint-Michielsgestel, tandheelkunde Berlicum, Tandzorg Gestel')
    }
  }
}
</script>

<style scoped>
.location-page {
  padding-top: 120px;
}

.hero-section {
  background: linear-gradient(135deg, #fff7e6 0%, #e8f4fd 100%);
  padding: 60px 0;
  margin-bottom: 40px;
}

.hero-section h1 {
  font-size: 2.5rem;
  color: var(--black);
  margin-bottom: 1rem;
  font-weight: 700;
}


.travel-note {
  font-size: 1.3rem;
  color: var(--light-color);
  margin-top: 0.8rem;
  font-style: italic;
}

.content-section h2 {
  font-size: 2.2rem;
  margin: 2rem 0 1rem;
}

.content-section h3 {
  font-size: 1.8rem;
  margin: 1.5rem 0 1rem;
}

.content-section p,
.content-section li {
  font-size: 1.6rem;
  line-height: 1.8;
}

.highlight-box {
  background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
  padding: 2rem;
  border-radius: 12px;
  margin: 2rem 0;
  border-left: 4px solid var(--blue);
}

.highlight-box h3 {
  color: var(--blue);
  margin-bottom: 1.5rem;
  font-size: 1.4rem;
}

.benefits-list {
  list-style: none;
  padding: 0;
}

.benefits-list li {
  padding: 0.5rem 0;
  color: var(--light-color);
  position: relative;
  padding-left: 1.5rem;
}

.benefits-list li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: var(--blue);
  font-weight: bold;
}

.route-section {
  margin: 3rem 0;
}

.route-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  height: 100%;
}

.route-card h4 {
  color: var(--blue);
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
}

.route-card ul {
  list-style: none;
  padding: 0;
}

.route-card li {
  padding: 0.8rem 0;
  border-bottom: 1px solid #f0f0f0;
  color: var(--light-color);
}

.route-card li:last-child {
  border-bottom: none;
}

.route-card strong {
  color: var(--black);
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: var(--blue);
  color: white;
  border: 2px solid var(--blue);
}

.btn-primary:hover {
  background: var(--dark-blue);
  transform: translateY(-2px);
}

.btn-secondary {
  background: white;
  color: var(--blue);
  border: 2px solid var(--blue);
}

.btn-secondary:hover {
  background: var(--blue);
  color: white;
}

.sidebar {
  position: sticky;
  top: 130px;
}

.contact-card,
.hours-card,
.specialties-card,
.emergency-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
}

.contact-card h3,
.hours-card h3,
.specialties-card h3,
.emergency-card h3 {
  color: var(--blue);
  margin-bottom: 1rem;
  font-size: 1.3rem;
}

.hours-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.hours-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.hours-row:last-child {
  border-bottom: none;
}

.day {
  font-weight: 600;
  color: var(--black);
}

.time {
  color: var(--light-color);
}

.specialties-list {
  list-style: none;
  padding: 0;
}

.specialties-list li {
  padding: 0.4rem 0;
  color: var(--light-color);
}

.emergency-card {
  background: linear-gradient(135deg, #fff5f5 0%, #ffebee 100%);
  text-align: center;
}

.emergency-btn {
  background: #dc3545;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.emergency-btn:hover {
  background: #c82333;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .location-page {
    padding-top: 100px;
  }

  .hero-section h1 {
    font-size: 2rem;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn {
    width: 100%;
    max-width: 250px;
    justify-content: center;
  }

  .sidebar {
    position: static;
    margin-top: 3rem;
  }

  .highlight-box,
  .route-card {
    padding: 1.5rem;
  }
}
</style>

